// src/guides/registry.js
// Host Playbook guide registry: pure normalize/merge/gate logic for the
// in-app training guides (spec: ~/.claude/plans/host-training-guides-framework.md).
// Deliberately framework-free and browser-free so scripts/guides.test.mjs can
// import it under plain node. The React side (useGuides) composes these with
// live config; everything here fails CLOSED — an invalid guide, unknown
// category or unmet gate renders nothing rather than a broken card.

// Explicit .js extension so scripts/guides.test.mjs can import this module
// under plain node ESM (Vite resolves it the same either way).
import { normalizeRole } from '../lib/auth/roles.js';
import { toNeutralPath } from '../routes/legacyPaths.js';

// Audience gating is EXACT-match on purpose — deliberately NOT
// roleIsAllowed, which is hierarchical (head_host outranks host) and would
// show a hosts-only step ("$5 per payer") to head hosts whose deal is a
// retainer. "audienceRoles" means who the content is FOR, not who ranks
// high enough to open the page; admin/management must be listed explicitly
// where they should preview a targeted step.
function audienceMatches(role, audienceRoles) {
  if (!audienceRoles || !audienceRoles.length) return true;
  const userRole = normalizeRole(role);
  if (!userRole) return false;
  return audienceRoles.some((r) => normalizeRole(r) === userRole);
}

// Fixed category set. Playbook renders sections in this order; a guide with
// an unknown category is dropped (fail closed) rather than shown uncategorized.
export const GUIDE_CATEGORIES = [
  { id: 'gettingStarted', title: 'Start Here', order: 1 },
  { id: 'promote', title: 'Promote & Grow', order: 2 },
  { id: 'bookings', title: 'Lists & Bookings', order: 3 },
  { id: 'door', title: 'On the Night', order: 4 },
  { id: 'pay', title: 'Getting Paid', order: 5 },
  { id: 'game', title: 'Missions & Perks', order: 6 }
];

const CATEGORY_IDS = new Set(GUIDE_CATEGORIES.map((c) => c.id));
const CATEGORY_ORDER = Object.fromEntries(GUIDE_CATEGORIES.map((c) => [c.id, c.order]));
const STATUSES = new Set(['draft', 'published', 'hidden']);
const MEDIA_TYPES = new Set(['image', 'video']);
// cta.action values the viewer knows how to perform (besides route/url).
const CTA_ACTIONS = new Set(['welcomeTour']);

const cleanString = (v) => String(v ?? '').trim();

// Dotted-path getter for requiresConfig gates (e.g. 'missions.enabled' into
// the normalized gamification config). Tiny on purpose — no lodash.
export function getConfigPath(obj, dottedPath) {
  const path = cleanString(dottedPath);
  if (!path) return undefined;
  let cur = obj;
  for (const key of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[key];
  }
  return cur;
}

function normalizeCta(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const label = cleanString(raw.label);
  // Saved overrides may still hold old /clubastra/... routes.
  const route = toNeutralPath(cleanString(raw.route));
  const url = cleanString(raw.url);
  const action = cleanString(raw.action);
  if (!label) return null;
  if (route) return { label, route };
  if (url) return { label, url, external: true };
  if (CTA_ACTIONS.has(action)) return { label, action };
  return null;
}

function normalizeMedia(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const type = cleanString(raw.type);
  const src = cleanString(raw.src);
  if (!MEDIA_TYPES.has(type) || !src) return null;
  return { type, src };
}

function normalizeStep(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const title = cleanString(raw.title);
  const body = cleanString(raw.body);
  if (!title && !body) return null;
  return {
    kicker: cleanString(raw.kicker),
    title,
    body,
    media: normalizeMedia(raw.media),
    cta: normalizeCta(raw.cta),
    // Per-step gates (same semantics as the guide-level ones): a step whose
    // gate fails is skipped; a guide whose steps ALL fail is not renderable.
    requiresFeature: cleanString(raw.requiresFeature),
    requiresConfig: cleanString(raw.requiresConfig),
    // Per-step role gate: one guide can tailor a step per host type (e.g.
    // the pay guide's THE DEAL step: $5/payer for hosts, retainer wording
    // for head hosts/ambassadors). Empty = every role sees it.
    audienceRoles: (Array.isArray(raw.audienceRoles) ? raw.audienceRoles : []).map(cleanString).filter(Boolean)
  };
}

// A guide is renderable iff it has id + title, a published-able status, a
// known category and at least one usable step. Anything else → null.
export function normalizeGuide(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = cleanString(raw.id);
  const title = cleanString(raw.title);
  const category = cleanString(raw.category);
  if (!id || !title || !CATEGORY_IDS.has(category)) return null;
  // Unknown status is treated as draft (fail closed), never as published.
  const status = STATUSES.has(raw.status) ? raw.status : 'draft';
  const steps = (Array.isArray(raw.steps) ? raw.steps : []).map(normalizeStep).filter(Boolean);
  if (!steps.length) return null;
  const order = Number(raw.order);
  return {
    id,
    app: cleanString(raw.app) || 'portal',
    title,
    category,
    order: Number.isFinite(order) ? order : 999,
    status,
    icon: cleanString(raw.icon),
    summary: cleanString(raw.summary),
    audienceRoles: (Array.isArray(raw.audienceRoles) ? raw.audienceRoles : []).map(cleanString).filter(Boolean),
    requiresFeature: cleanString(raw.requiresFeature),
    requiresConfig: cleanString(raw.requiresConfig),
    anchors: (Array.isArray(raw.anchors) ? raw.anchors : [])
      .map(cleanString)
      .filter(Boolean)
      .map((a) => (a.startsWith('route:') ? `route:${toNeutralPath(a.slice(6))}` : a)),
    steps
  };
}

// Merge raw guide layers by id — later layers REPLACE the whole doc (no deep
// merge, so an override is always a complete, reviewable guide). An override
// of `{ id, status: 'hidden' }` removes the guide entirely. Runs BEFORE
// normalize so a bare hidden marker doesn't need title/steps.
export function mergeGuides(...layers) {
  const byId = new Map();
  for (const layer of layers) {
    for (const raw of Array.isArray(layer) ? layer : []) {
      const id = cleanString(raw?.id);
      if (id) byId.set(id, raw);
    }
  }
  return Array.from(byId.values()).filter((raw) => raw?.status !== 'hidden');
}

// ctx = { role, eventConfig, gamificationConfig } with role already
// normalized (normalizeRole) by the caller.
function gatePasses(requiresFeature, requiresConfig, ctx) {
  if (requiresFeature && ctx?.eventConfig?.features?.[requiresFeature] !== true) return false;
  if (requiresConfig && !getConfigPath(ctx?.gamificationConfig, requiresConfig)) return false;
  return true;
}

export function visibleSteps(guide, ctx) {
  return (guide?.steps || []).filter(
    (s) => gatePasses(s.requiresFeature, s.requiresConfig, ctx) && audienceMatches(ctx?.role, s.audienceRoles)
  );
}

export function isGuideVisible(guide, ctx) {
  if (!guide || guide.status !== 'published' || guide.app !== 'portal') return false;
  if (!audienceMatches(ctx?.role, guide.audienceRoles)) return false;
  if (!gatePasses(guide.requiresFeature, guide.requiresConfig, ctx)) return false;
  return visibleSteps(guide, ctx).length > 0;
}

// Full pipeline: raw layers → merged → normalized → visible, sorted for the
// Playbook (category order, then per-guide order, then title).
export function resolveVisibleGuides(rawLayers, ctx) {
  return mergeGuides(...rawLayers)
    .map(normalizeGuide)
    .filter(Boolean)
    .filter((g) => isGuideVisible(g, ctx))
    .sort(
      (a, b) =>
        (CATEGORY_ORDER[a.category] || 99) - (CATEGORY_ORDER[b.category] || 99) || a.order - b.order || a.title.localeCompare(b.title)
    );
}

export function guidesForAnchor(guides, anchor) {
  const wanted = cleanString(anchor);
  if (!wanted) return [];
  return (guides || []).filter((g) => g.anchors.includes(wanted));
}
